import { ENTITY_TYPE, PREVIEW_TYPE } from '../utils/constants';
import { WritableDraft } from '../utils/immer';
import { Enum, PreviewDetails } from '../utils/types';
import { createImmerStore } from './createStore';
import { EntityStore, entityStore } from './entityStore';
import { LeadCaptureStore, leadCaptureStore } from './leadCaptureStore';
import { NotificationStore, notificationStore } from './notificationStore';
import { VideoStore, videoStore } from './videoStore';

export type GlobalStore = {
  entityOwnerId: number | null;
  entityType: Enum<typeof ENTITY_TYPE> | null;
  /**
   * You probably do no need to use this, but rather apiEntityId.
   * {@link GlobalStore.apiEntityId}
   *
   * Note:
   * In case of live event, entityId and eventId are same
   */
  entityId: string;
  /**
   * You probably do no need to use this, but rather apiEventId.
   * {@link GlobalStore.apiEventId}
   *
   * Note:
   * In case of live event, entityId and eventId are same
   */
  eventId: string | null;
  hasNewLeadCaptureFlow: boolean | null;
  previewType: Enum<typeof PREVIEW_TYPE>;
  previewDetails: PreviewDetails;
  currentSelectedFieldIndex: number;
  // Allows us to use emailaddress across multiple preview types without typing again
  emailAddressCached: string;
  baseUrl: string;
  /**
   * Cookie will contain the registrant id (lead id). Need to set the value after registration or login.
   * This value will be set at time of page load if the cookie is present in browser
   */
  cookie?: string;
  unlistedHash?: string | null;
  /**
   * apiEventId will include the event id along with unlisted hash (in case of unlisted event)
   * Eg:
   * If unlisted event -> 123456:kjn2345287
   * If not unlisted event -> 123456
   *
   * Note:
   * In case of live event, apiEventId and apiEntityId are same
   */
  apiEventId: string | null;
  /**
   * apiEntityId will include the entity id along with unlisted hash (in case of unlisted entity)
   * Eg:
   * If unlisted entity -> 123456:kjn2345287
   * If not unlisted entity -> 123456
   *
   * Note:
   * In case of live event, apiEventId and apiEntityId are same
   */
  apiEntityId: string;
  parentLeadUuid?: string;
  setEntityType: (entity: Enum<typeof ENTITY_TYPE>) => void;
  setEntityId: (entityId: string) => void;
  setEventId: (eventId: string) => void;
  setEntityOwnerId: (entityOwnerId: number | undefined) => void;
  setPreviewType: (previewType: Enum<typeof PREVIEW_TYPE>) => void;
  setPreviewDetails: (previewMode: PreviewDetails) => void;
  setCurrentSelectedFieldIndex: (currentSelectedFieldIndex: number) => void;
  setEmailAddressCached: (emailAddressCached: string) => void;
} & LeadCaptureStore &
  EntityStore &
  VideoStore &
  NotificationStore;

export type ZustandAPI = {
  set: <A extends string | { type: unknown }>(
    nextStateOrUpdater:
      | GlobalStore
      | Partial<GlobalStore>
      | ((state: WritableDraft<GlobalStore>) => void),
    shouldReplace?: boolean | undefined,
    action?: A | undefined,
  ) => void;
  get: () => GlobalStore;
};

export const useGlobalStore = createImmerStore<GlobalStore>((set, get) => {
  const stateDefinitionArgs = {
    set,
    get,
  } as ZustandAPI;

  const store = {
    ...leadCaptureStore(stateDefinitionArgs),
    ...entityStore(stateDefinitionArgs),
    ...videoStore(stateDefinitionArgs),
    ...notificationStore(stateDefinitionArgs),
    entityType: null,
    entityId: '0',
    eventId: null,
    entityOwnerId: null,
    previewType: PREVIEW_TYPE.FORM,
    previewDetails: {},
    currentSelectedFieldIndex: 0,
    emailAddressCached: '',
    baseUrl: '',
    hasNewLeadCaptureFlow: false,
    apiEventId: '',
    apiEntityId: '',
    setEntityType: (entityType: Enum<typeof ENTITY_TYPE>) => set(() => ({ entityType })),
    setEntityId: (entityId: string) => set(() => ({ entityId })),
    setEventId: (eventId: string) => set(() => ({ eventId })),
    setEntityOwnerId: (entityOwnerId: number | undefined) => set(() => ({ entityOwnerId })),
    setPreviewType: (previewType: Enum<typeof PREVIEW_TYPE>) => set(() => ({ previewType })),
    setPreviewDetails: (previewDetails: PreviewDetails) =>
      set(() => ({ previewDetails: { ...get().previewDetails, ...previewDetails } })),
    setCurrentSelectedFieldIndex: (currentSelectedFieldIndex: number) =>
      set(() => ({ currentSelectedFieldIndex })),
    setEmailAddressCached: (emailAddressCached: string) => set(() => ({ emailAddressCached })),
  };

  return store;
});
